import type { Row } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import * as React from 'react';
import { DEFAULT_NO_DATA_MESSAGE, DEFAULT_SKELETON_ROW_COUNT } from '../../data-grid.constants';
import type { DataGridRowsHeight } from '../../data-grid.type';
import { getRowsMinHeight } from '../../util/rows';
import { resolveDataGridCellMaxWidth, resolveDataGridCellWidth } from './cell-width';
import type { IDataGridColumnLike } from './data-grid-column-like.type';
import { DataGridSkeletonRows } from './data-grid-skeleton-rows';
import { Tooltip, TooltipContent, TooltipPortal, TooltipProvider, TooltipTrigger } from '@components/atoms';
import {
	type DataGridRow,
	type DataGridStrings,
	TablePrimitiveBody,
	TablePrimitiveRow,
	TablePrimitiveCell,
} from '@components/organisms';
import { cn } from '@utils';

const ROW_DISABLED_TOOLTIP_DELAY_MS = 100;

/**
 * Props for the DataGridBody component
 * @interface DataGridBodyProps
 *
 * @property {IDataGridColumnLike[]} columns - Array of column definitions for the data grid
 *   Defines the structure and behavior of each column in the table
 *
 * @property {boolean} [hasRowDisablement] - Whether the grid was configured with `disableRowSelection` at all.
 *   When falsy, rows skip the per-row `Tooltip`/`TooltipTrigger` wrapper entirely, since none of them can ever
 *   show a disabled-row tooltip. This is a grid-level flag, not a per-row one, so toggling it does not cause
 *   the per-row remounts that a per-row-dynamic condition would.
 *
 * @property {boolean} [isLoading] - Whether the table is in a loading state
 *   When true, displays loadingMessage from strings prop instead of table content
 *
 * @property {(row: T) => void} [onRowClick] - Callback function when a row is clicked
 *   - Receives the original row data as parameter
 *   - When provided, adds pointer cursor to rows
 *
 * @property {('single'|'multiple')} [rowSelection] - Type of row selection allowed
 *   - 'single': Only one row can be selected at a time
 *   - 'multiple': Multiple rows can be selected simultaneously
 *
 * @property {DataGridStrings} [strings] - Localization strings for the data grid
 *   Contains messages for loading, empty states, and subject replacement
 *
 * @property {string} [tableMinHeight] - Minimum height for the table body
 *   Applied to empty state and loading state containers
 *
 * @property {Row<T>[]} tableRows - Array of processed row data
 *   Contains the actual data to be displayed in the table
 */
interface DataGridBodyProps<T> {
	columns: IDataGridColumnLike[];
	hasRowDisablement?: boolean;
	isLoading?: boolean;
	onRowClick?: (row: DataGridRow<T>) => void;
	pageSize?: number;
	rowsHeight?: DataGridRowsHeight | number;
	rowSelection?: 'single' | 'multiple';
	strings?: DataGridStrings;
	tableMinHeight?: string;
	tableRows: Row<DataGridRow<T>>[];
}

/**
 * A flexible table body component for displaying data in a grid format with support for
 * loading states, empty states, row selection, and row click handling
 *
 * @component
 * @template T - Type of the data being displayed in the table
 *
 * @example
 * // Basic usage with required props
 * <DataGridBody
 *   columns={columns}
 *   tableRows={rows}
 * />
 *
 * @example
 * // With loading state and localization
 * <DataGridBody
 *   columns={columns}
 *   tableRows={rows}
 *   isLoading={true}
 *   strings={{
 *     loadingMessage: "Loading data...",
 *     subject: "items"
 *   }}
 * />
 *
 * @example
 * // With row selection and click handling
 * <DataGridBody
 *   columns={columns}
 *   tableRows={rows}
 *   rowSelection="multiple"
 *   onRowClick={(row) => console.log('Clicked row:', row)}
 * />
 */
const DataGridBodyComponent = <T,>({
	columns,
	hasRowDisablement,
	isLoading,
	onRowClick,
	pageSize,
	rowsHeight = 'md',
	rowSelection,
	strings,
	tableMinHeight,
	tableRows,
}: DataGridBodyProps<T>) => {
	const handleOnRowClick = React.useCallback(
		(event: React.MouseEvent<HTMLTableRowElement>, row: Row<DataGridRow<T>>) => {
			if (onRowClick && !row.original.__isDisabled) {
				onRowClick(row.original);
			}
		},
		[onRowClick]
	);

	const rowHeight = `${getRowsMinHeight(rowsHeight)}px`;
	const rowHeightStyle: React.CSSProperties = { height: rowHeight, minHeight: rowHeight, maxHeight: rowHeight };
	// Native table cells ignore `max-height`, so the only reliable way to keep tall content from
	// growing a row is a fixed-height, clipped wrapper. The cell's own vertical padding is removed
	// so the cell box equals the row height exactly; content is centered and any overflow is hidden.
	const clampedCellStyle: React.CSSProperties = { paddingTop: 0, paddingBottom: 0 };
	const clampedCellContentStyle: React.CSSProperties = {
		height: rowHeight,
		overflow: 'hidden',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
	};
	const clampedActionsCellContentStyle: React.CSSProperties = {
		...clampedCellContentStyle,
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'flex-end',
	};
	const maxVisibleRows = pageSize && pageSize > 0 ? pageSize : undefined;

	if (isLoading) {
		// Fill the measured page size with skeletons when there are no current rows, so the loading
		// state matches the row count the grid actually renders instead of a fixed fallback.
		const fallbackSkeletonRowCount = maxVisibleRows ?? DEFAULT_SKELETON_ROW_COUNT;
		const skeletonRowCount = Math.min(tableRows.length || fallbackSkeletonRowCount, maxVisibleRows ?? Infinity);

		return (
			<TablePrimitiveBody data-aid="data-grid-body">
				<DataGridSkeletonRows
					columns={columns}
					rowCount={skeletonRowCount}
					rowHeight={rowHeight}
					rowSelection={rowSelection}
				/>
			</TablePrimitiveBody>
		);
	}

	return (
		<TooltipProvider>
			<TablePrimitiveBody data-aid="data-grid-body">
				{tableRows.length ? (
					tableRows.map(row => {
						const rowSelected = row.getIsSelected();
						const visibleCells = row.getVisibleCells() ?? [];
						const isDisabled = Boolean(row.original.__isDisabled);
						const disabledTooltip = row.original.__disabledTooltip;
						const isFocusableForDisabledTooltip = isDisabled && Boolean(disabledTooltip);

						const rowElement = (
							<TablePrimitiveRow
								className={cn(onRowClick && !isDisabled && 'cursor-pointer', isDisabled && 'cursor-default')}
								style={rowHeightStyle}
								data-state={rowSelected && 'selected'}
								onClick={e => onRowClick && handleOnRowClick(e, row)}
								tabIndex={isFocusableForDisabledTooltip ? 0 : undefined}
								aria-disabled={isDisabled || undefined}
							>
								{visibleCells.map((cell, i) => {
									const cellContext = cell.getContext();
									const isFirstColumn = i === 0;
									const isActionsColumn = cellContext.column.id === 'actions';
									const fixedColumnStyle = resolveDataGridCellWidth({
										isFirstColumn,
										isActionsColumn,
										hasRowSelection: Boolean(rowSelection),
										hasExplicitWidth: cell.column.columnDef.meta?.hasExplicitWidth ?? false,
										size: cell.column.getSize(),
									});
									const cellStyle: React.CSSProperties = {
										...fixedColumnStyle,
										...clampedCellStyle,
									};
									const renderedCell = flexRender(cell.column.columnDef.cell, cellContext);
									const clampContentStyle = isActionsColumn ? clampedActionsCellContentStyle : clampedCellContentStyle;
									const maxWidth = resolveDataGridCellMaxWidth(fixedColumnStyle);

									return (
										<TablePrimitiveCell
											data-aid={`data-grid-cell-${cellContext.column.id}`}
											key={cell.id}
											maxWidth={maxWidth}
											overflow="hidden"
											style={cellStyle}
											className={cn(isActionsColumn && 'text-right')}
										>
											<div style={clampContentStyle} className={cn(isDisabled && 'opacity-50')}>
												{renderedCell}
											</div>
										</TablePrimitiveCell>
									);
								})}
							</TablePrimitiveRow>
						);

						if (!hasRowDisablement) {
							return <React.Fragment key={row.id}>{rowElement}</React.Fragment>;
						}

						return (
							<Tooltip
								key={row.id}
								delayDuration={ROW_DISABLED_TOOLTIP_DELAY_MS}
								open={isFocusableForDisabledTooltip ? undefined : false}
							>
								<TooltipTrigger asChild>{rowElement}</TooltipTrigger>
								{isDisabled && disabledTooltip && (
									<TooltipPortal>
										<TooltipContent>{disabledTooltip}</TooltipContent>
									</TooltipPortal>
								)}
							</Tooltip>
						);
					})
				) : (
					<TableFiller columns={columns} tableMinHeight={tableMinHeight}>
						{parseEmptyState({ strings })}
					</TableFiller>
				)}
			</TablePrimitiveBody>
		</TooltipProvider>
	);
};

export const DataGridBody = React.memo(DataGridBodyComponent) as typeof DataGridBodyComponent;

/**
 * Parses the empty state message, replacing {{subject}} placeholder if present
 * @param {Object} params - Parameters for empty state parsing
 * @param {DataGridStrings} [params.strings] - Strings containing subject for replacement
 * @returns {string} The processed empty state message
 */
function parseEmptyState({ strings }: { strings?: DataGridStrings }) {
	const noDataMessage = strings?.noDataMessage || DEFAULT_NO_DATA_MESSAGE;
	const subject = strings?.subject || '';

	return noDataMessage && strings?.subject ? (noDataMessage as string).replace('{{subject}}', subject) : '';
}

/**
 * Renders a table row that spans all columns for displaying loading or empty states
 * @param {Object} props - The component props
 * @param {React.ReactNode} props.children - Content to display in the filler row
 * @param {IDataGridColumnLike[]} props.columns - Array of column definitions to determine colspan
 * @param {string} [props.tableMinHeight] - Minimum height for the filler row
 */
function TableFiller({
	children,
	columns,
	tableMinHeight,
}: {
	children: React.ReactNode;
	columns: IDataGridColumnLike[];
	tableMinHeight?: string;
}) {
	return (
		<TablePrimitiveRow>
			<td
				colSpan={columns.length}
				className={cn('w-full text-center')}
				style={{
					minHeight: tableMinHeight,
					height: tableMinHeight,
					maxHeight: tableMinHeight,
				}}
			>
				{children}
			</td>
		</TablePrimitiveRow>
	);
}
